import React, { useState, useEffect } from 'react';
import { 
  GraduationCap, Volume2, Star, CheckCircle2, Calendar, 
  BookOpen, Trophy, MessageCircle, Heart, Sparkles, User, Play, UserCheck, ShieldCheck, Lock,
  VolumeX, Smile, Zap, Compass, FileText, Download, Award
} from 'lucide-react';
import { UserSession } from '../AppShell';
import { DEMO_STUDENTS, DemoStudent, getAccessibleStudents } from '../data/demoStudents';

interface StudentPortalDashboardProps {
  currentUser?: UserSession | null;
  onOpenAAC?: () => void;
}

export const StudentPortalDashboard: React.FC<StudentPortalDashboardProps> = ({ currentUser, onOpenAAC }) => {
  const [activeTab, setActiveTab] = useState<'goals' | 'timetable' | 'mood' | 'rewards'>('goals');
  const [speakingText, setSpeakingText] = useState<string | null>(null);
  const [selectedMood, setSelectedMood] = useState<string | null>('Calm & Focused');
  
  const accessibleStudents = getAccessibleStudents(currentUser);
  const [selectedStudentId, setSelectedStudentId] = useState<string>(accessibleStudents[0]?.id || DEMO_STUDENTS[0].id);

  // Auto-detect and lock profile if logged in as a specific student
  useEffect(() => {
    const freshAccessible = getAccessibleStudents(currentUser);
    if (!freshAccessible.some(s => s.id === selectedStudentId)) {
      setSelectedStudentId(freshAccessible[0]?.id || DEMO_STUDENTS[0].id);
    }
  }, [currentUser]);

  const activeStudent: DemoStudent = accessibleStudents.find(s => s.id === selectedStudentId) || accessibleStudents[0] || DEMO_STUDENTS[0];
  const isDirectStudentLogin = currentUser?.role === 'STUDENT';

  // Text-to-Speech (TTS) Audio Reader Helper
  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingText(text);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9; // Slightly slower for clear accessibility comprehension
      utterance.pitch = 1.0;
      utterance.onend = () => setSpeakingText(null);
      utterance.onerror = () => setSpeakingText(null);
      window.speechSynthesis.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setSpeakingText(null);
    }
  };

  const goals = [
    { title: activeStudent.iepGoal, category: 'Communication / IEP Target', progress: activeStudent.overallMasteryPercent, status: 'On Track' },
    { title: 'Independent Handwashing & Daily Task Analysis', category: 'Self Care & Living', progress: 90, status: 'Mastered' },
    { title: '/r/ and /s/ Sound Articulation & Voice Pitch Matching', category: 'Speech Pathology', progress: 78, status: 'In Progress' }
  ];

  const timetable = [
    { time: '09:00 AM', subject: 'Morning AAC Circle & Symbol Greeting', teacher: 'Mrs. Davis', room: 'Room 102' },
    { time: '10:15 AM', subject: 'Individual Speech Pathology Drill', teacher: activeStudent.therapist, room: 'Therapy Suite B' },
    { time: '11:30 AM', subject: 'Sensory Room Snoezelen Soothing', teacher: 'OT Team', room: 'Sensory Room' },
    { time: '01:30 PM', subject: 'Adaptive Music Therapy & RAS Metronome', teacher: 'Mr. Harrison', room: 'Activity Hall' }
  ];

  const moods = [
    { label: 'Calm & Focused', emoji: '😊', bg: 'bg-emerald-500/20 border-emerald-400/40 text-emerald-900', sensoryRec: 'Deep Blue Lavender Snoezelen Mode' },
    { label: 'Energetic & Excited', emoji: '🌟', bg: 'bg-amber-500/20 border-amber-400/40 text-amber-900', sensoryRec: 'Rhythmic Beat Metronome Therapy' },
    { label: 'Tired or Overwhelmed', emoji: '😴', bg: 'bg-blue-500/20 border-blue-400/40 text-blue-900', sensoryRec: 'Dim Rain Soundscape & Weighted Blanket' },
    { label: 'Anxious / Sensory Seeking', emoji: '🧩', bg: 'bg-purple-500/20 border-purple-400/40 text-purple-900', sensoryRec: 'Tactile Braille & Hydrotherapy Warm Bubble' }
  ];

  return (
    <div className="py-6 px-4 max-w-7xl mx-auto space-y-6">
      
      {/* Student Profile Header Bar */}
      <div className="bg-amber-50 border border-amber-200 p-3 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-3 text-xs shadow-sm">
        
        {isDirectStudentLogin ? (
          /* Locked Direct Student Profile Badge (No Switcher Dropdown) */
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-emerald-600 text-white rounded-lg flex items-center justify-center shadow">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <span className="font-extrabold text-slate-800">Authenticated Student Profile: </span>
              <strong className="text-blue-900 font-black">{activeStudent.name}</strong>
              <span className="text-slate-500 font-mono text-[11px]"> ({activeStudent.rollId})</span>
            </div>
          </div>
        ) : (
          /* Admin / Educator Switcher (Filtered by Tenant for School Admins, All for Super Admin) */
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-amber-700 shrink-0" />
            <span className="font-bold text-slate-800">
              {currentUser?.role === 'SUPER_ADMIN' ? 'Student Portal Profile (Super Admin Mode):' : `Student Portal Profile (${currentUser?.tenant || 'School'}):`}
            </span>
            <select 
              value={selectedStudentId}
              onChange={(e) => setSelectedStudentId(e.target.value)}
              className="px-2.5 py-1 bg-white border border-amber-300 rounded-lg font-bold text-amber-900 focus:ring-2 focus:ring-amber-500 shadow-sm"
            >
              {accessibleStudents.map(st => (
                <option key={st.id} value={st.id}>{st.name} ({st.rollId}) - {st.disabilityCategory}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2">
          {speakingText ? (
            <button
              onClick={stopSpeaking}
              className="px-3 py-1 bg-red-500 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 animate-pulse shadow"
            >
              <VolumeX className="w-3.5 h-3.5" /> Stop Audio Reader
            </button>
          ) : (
            <button
              onClick={() => speakText(`Welcome back ${activeStudent.name}. Your active goal is: ${activeStudent.iepGoal}`)}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs flex items-center gap-1.5 shadow"
            >
              <Volume2 className="w-3.5 h-3.5" /> Read Welcome Aloud
            </button>
          )}
        </div>
      </div>

      {/* Student Portal Hero Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 text-white p-6 rounded-3xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 border border-blue-500/30">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/10 border-2 border-amber-400 rounded-2xl flex items-center justify-center text-amber-300 font-black text-3xl shadow-inner">
            🎓
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 bg-amber-400/20 text-amber-300 border border-amber-400/30 text-[10px] font-extrabold rounded-full uppercase tracking-wider">
                Student Self-Service Portal
              </span>
              <span className="text-xs text-blue-200">{activeStudent.grade}</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black mt-1">
              Welcome back, {activeStudent.name}!
            </h1>
            <p className="text-xs text-blue-200 mt-1">
              Disability Category: <span className="text-amber-300 font-bold">{activeStudent.disabilityCategory}</span> • Roll ID: <span className="font-mono text-white font-bold">{activeStudent.rollId}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onOpenAAC}
            className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-2xl text-xs shadow-lg transition flex items-center gap-2"
          >
            <Volume2 className="w-4 h-4 text-amber-300" />
            <span>Launch AAC Speech Board</span>
          </button>
        </div>
      </div>

      {/* Portal Tabs Navigation */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('goals')}
          className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center gap-2 ${
            activeTab === 'goals' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <GraduationCap className="w-4 h-4" /> My IEP Learning Goals
        </button>

        <button
          onClick={() => setActiveTab('timetable')}
          className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center gap-2 ${
            activeTab === 'timetable' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Calendar className="w-4 h-4" /> Daily Schedule & Classes
        </button>

        <button
          onClick={() => setActiveTab('mood')}
          className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center gap-2 ${
            activeTab === 'mood' ? 'bg-purple-600 text-white shadow-md' : 'bg-purple-50 text-purple-800 border border-purple-200 hover:bg-purple-100'
          }`}
        >
          <Smile className="w-4 h-4 text-purple-600" /> Daily Mood Check-In
        </button>

        <button
          onClick={() => setActiveTab('rewards')}
          className={`px-4 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center gap-2 ${
            activeTab === 'rewards' ? 'bg-amber-500 text-slate-950 shadow-md' : 'bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100'
          }`}
        >
          <Trophy className="w-4 h-4 text-amber-600" /> Token Star Rewards
        </button>
      </div>

      {/* TAB 1: IEP LEARNING GOALS */}
      {activeTab === 'goals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-900">Current Progress on IEP Goals for {activeStudent.name}</h2>
            <button
              onClick={() => speakText(`Your IEP goals for today are: ${goals.map(g => `${g.title}, progress ${g.progress} percent`).join('. ')}`)}
              className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-blue-100 transition"
            >
              <Volume2 className="w-4 h-4 text-blue-600" /> Listen to Goals
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {goals.map((g, idx) => (
              <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3 relative hover:shadow-md transition">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-extrabold text-slate-500 uppercase tracking-wider text-[10px]">{g.category}</span>
                  <span className="font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[10px]">{g.status}</span>
                </div>

                <h3 className="font-bold text-slate-900 text-sm leading-snug">{g.title}</h3>

                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-xs text-slate-500 font-bold">
                    <span>Mastery Progress</span>
                    <span className="text-blue-600 font-mono">{g.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${g.progress}%` }} />
                  </div>
                </div>

                <button
                  onClick={() => speakText(`Goal ${idx + 1}: ${g.title}. Current progress is ${g.progress} percent.`)}
                  className="w-full py-1.5 bg-slate-50 hover:bg-slate-100 text-slate-600 font-bold text-xs rounded-xl flex items-center justify-center gap-1 border border-slate-200 transition"
                >
                  <Volume2 className="w-3.5 h-3.5 text-blue-600" /> Read Goal Aloud
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: DAILY SCHEDULE & CLASSES */}
      {activeTab === 'timetable' && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-slate-900">Today's Schedule for {activeStudent.name}</h2>
            <button
              onClick={() => speakText(`Your schedule for today: ${timetable.map(t => `${t.time}, ${t.subject} with ${t.teacher} in ${t.room}`).join('. ')}`)}
              className="px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-bold flex items-center gap-1.5 hover:bg-blue-100 transition"
            >
              <Volume2 className="w-4 h-4 text-blue-600" /> Read Schedule Aloud
            </button>
          </div>

          <div className="space-y-3">
            {timetable.map((t, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-blue-300 transition">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-600 text-white font-mono font-bold text-xs rounded-xl shadow-sm">
                    {t.time}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">{t.subject}</div>
                    <div className="text-xs text-slate-500">Educator / Therapist: <strong className="text-slate-800">{t.teacher}</strong></div>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto">
                  <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-xl border border-indigo-200">
                    {t.room}
                  </span>
                  <button
                    onClick={() => speakText(`At ${t.time}, ${t.subject} with ${t.teacher} in ${t.room}`)}
                    className="p-2 bg-white hover:bg-blue-50 text-blue-600 rounded-xl border border-slate-200 shadow-sm"
                    title="Read Class Aloud"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: DAILY MOOD & SENSORY CHECK-IN */}
      {activeTab === 'mood' && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-6">
          <div>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-extrabold rounded-full uppercase tracking-wider">
              Sensory Accommodations & Emotional Regulation
            </span>
            <h2 className="text-xl font-black text-slate-900 mt-2">How are you feeling today, {activeStudent.name}?</h2>
            <p className="text-xs text-slate-500 mt-1">Select your mood to receive customized sensory room and therapy adjustments.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {moods.map((m, idx) => {
              const isSelected = selectedMood === m.label;

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedMood(m.label)}
                  className={`p-5 rounded-2xl border text-left space-y-3 transition transform active:scale-95 ${
                    isSelected 
                      ? 'bg-purple-600 text-white border-purple-500 shadow-lg ring-4 ring-purple-300' 
                      : 'bg-slate-50 text-slate-800 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <div className="text-4xl">{m.emoji}</div>
                  <div>
                    <div className="font-extrabold text-sm">{m.label}</div>
                    <div className={`text-[11px] mt-1 font-medium ${isSelected ? 'text-purple-100' : 'text-slate-500'}`}>
                      {m.sensoryRec}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedMood && (
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-2xl flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600 shrink-0" />
                <div>
                  <div className="font-bold text-purple-950">Active Mood Recorded: {selectedMood}</div>
                  <div className="text-purple-800">Sensory room preset & ambient music adjusted automatically.</div>
                </div>
              </div>
              <button
                onClick={() => speakText(`Your mood is recorded as ${selectedMood}. Sensory room environment has been adjusted.`)}
                className="px-3 py-1.5 bg-purple-600 text-white font-bold rounded-xl shadow text-xs flex items-center gap-1.5"
              >
                <Volume2 className="w-3.5 h-3.5" /> Read Aloud
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 4: TOKEN STAR REWARDS */}
      {activeTab === 'rewards' && (
        <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white p-8 rounded-3xl shadow-xl text-center space-y-6 relative overflow-hidden">
          <div className="w-20 h-20 bg-white/20 border-4 border-amber-300 rounded-full flex items-center justify-center mx-auto shadow-2xl text-4xl animate-bounce">
            ⭐
          </div>

          <div className="space-y-2">
            <h2 className="text-3xl font-black">Token Star Reward Board</h2>
            <p className="text-xs text-amber-100 max-w-md mx-auto font-medium">
              Great job, <strong className="text-white font-black">{activeStudent.name}</strong>! You have earned 14 Token Stars this week for completing speech articulation drills and IEP targets!
            </p>
          </div>

          <div className="flex items-center justify-center gap-3 py-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-12 h-12 bg-white/20 border-2 border-amber-300 rounded-2xl flex items-center justify-center text-3xl shadow-lg hover:scale-110 transition">
                ⭐
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button
              onClick={() => speakText(`Congratulations ${activeStudent.name}! You earned 14 Token Stars this week!`)}
              className="px-5 py-2.5 bg-slate-950 text-amber-400 font-extrabold text-xs rounded-xl shadow-lg hover:bg-slate-900 transition flex items-center gap-2"
            >
              <Volume2 className="w-4 h-4 text-amber-300" /> Hear Celebration
            </button>

            <button
              onClick={() => alert(`Certificate of Achievement generated for ${activeStudent.name} (Roll ID: ${activeStudent.rollId})!`)}
              className="px-5 py-2.5 bg-white text-slate-950 font-extrabold text-xs rounded-xl shadow-lg hover:bg-amber-100 transition flex items-center gap-2"
            >
              <Award className="w-4 h-4 text-amber-600" /> Print Achievement Certificate
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
